import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px);
  position: relative;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px);
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 8px;
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

const OptimalPaceIcon = styled.img`
  display: block;
  margin: ${theme.spacing.lg} auto 0 auto;
  width: 130px;
  height: 86px;
  object-fit: contain;
  object-position: center;
`;

const OptimalPaceHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: calc(${theme.typography.sizes.h2} * .9);
  font-weight: ${theme.typography.weights.semiBold};
  color: #435F60;
  text-align: center;
  margin: calc(${theme.spacing.sm} * 0) 0 0 0;
  width: 100%;
`;

const NumberContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${theme.spacing.md} 0 0 0;
  width: 100%;
`;

const OptimalPaceNumber = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 11.2rem;
  font-weight: ${theme.typography.weights.bold};
  color: #435F60;
  margin: 0;
  text-align: center;
  line-height: 1;
  user-select: none;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 8.4rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 5.6rem;
  }
`;

/**
 * Optimal Pace Isolated — display-only Optimal Pace number.
 * No +/- hour controls and no next/CTA button.
 */
const OptimalPaceIsolatedScreen = ({ data, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);

  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      setOptimalPaceData(newOptimalPaceData);
    }
  }, [userProgress]);

  const htmlContent = formatTextContent(data.content?.text, userProgress);

  if (!optimalPaceData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}

          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry with hours worked to see your optimal pace calculation.
            </NoDataText>
          </NoDataMessage>
        </Content>
      </Container>
    );
  }

  const optimalPaceNumber = Math.round(optimalPaceData.optimalHours);

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}

        <OptimalPaceIcon
          src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Optimal%20Pace.svg?alt=media&token=a0cd773e-b5f5-47f0-bd5d-7b95011315d4"
          alt="Optimal Pace"
        />

        <OptimalPaceHeading>Optimal Pace</OptimalPaceHeading>

        <NumberContainer>
          <OptimalPaceNumber>{optimalPaceNumber}</OptimalPaceNumber>
        </NumberContainer>
      </Content>
    </Container>
  );
};

export default OptimalPaceIsolatedScreen;
